<template>
  <div
    v-if="loading"
    class="app-spinner"
  >
    <c-icon
      type="loading"
      spin
      size="28"
      :color="$colors.grey.lighten1"
    />
  </div>
  <template v-else-if="!loading && currentUser === null">
    <app-header :page="loginPage" />
    <auth-view
      title="Log in"
      mode="login"
      context="sidepanel"
    />
  </template>
  <div
    v-else
    :style="appStyle"
  >
    <app-content :style="contentStyle" />
  </div>
  <c-toast />
</template>

<script>
import AppHeader from 'shared/components/AppHeader.vue';
import AuthView from 'shared/components/AuthView.vue';
import CIcon from 'shared/components/CIcon.vue';
import CToast from 'shared/components/CToast.vue';
import { FIREBASE_APP, NOTION_CLIENT, SUBSCRIPTION_MANAGER, USER_SERVICE, WORKSPACE_SERVICE } from 'shared/constants';
import useNotionClient from 'shared/services/notion-client';
import useCurrentUser from 'shared/services/user';
import useWorkspace from 'shared/services/workspace';
import { computed, inject, provide } from 'vue';

import AppContent from '@/components/AppContent.vue';

export default {
  name: 'App',
  setup() {
    const firebaseApp = inject(FIREBASE_APP);
    const subscriptionManager = inject(SUBSCRIPTION_MANAGER);
    const userService = useCurrentUser(firebaseApp.db, subscriptionManager);
    provide(USER_SERVICE, userService);
    const notionClient = useNotionClient(firebaseApp.functions);
    provide(NOTION_CLIENT, notionClient);
    const workspaceService = useWorkspace(firebaseApp.db, userService.currentUser, notionClient, subscriptionManager);
    provide(WORKSPACE_SERVICE, workspaceService);
    return {
      loading: computed(() => userService.loading.value || workspaceService.workspacesLoading.value),
      userLoading: userService.loading,
      currentUser: userService.currentUser,
      workspaceService,
    };
  },
  components: { AuthView, AppHeader, CIcon, AppContent, CToast },
  computed: {
    loginPage() {
      return {
        left: { component: 'logo' },
        center: { component: '' },
        right: { component: '' },
      };
    },
    contentStyle() {
      if (import.meta.env.DEV && import.meta.env.VITE_CONTEXT !== 'extension') {
        return {
          width: '40rem',
          height: '60rem',
          border: '1px solid black',
        };
      }
      return {};
    },
    appStyle() {
      if (import.meta.env.DEV && import.meta.env.VITE_CONTEXT !== 'extension') {
        return {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100vw',
          height: '100vh',
        };
      }
      return {};
    },
  },
  watch: {
    currentUser: {
      handler(newVal, oldVal) {
        if (newVal !== null && oldVal === null) {
          this.workspaceService.init();
        }
      },
      deep: true,
    },
  },
};

</script>
<style
    type="text/scss"
    lang="scss"
>
html {
  margin: 0;
  font-size: 62.5%;
  text-size-adjust: 100%;
  -ms-overflow-style: scrollbar;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "SF Pro Display", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Helvetica Neue", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-size: $font-size-base;
  -webkit-font-smoothing: subpixel-antialiased;
  text-size-adjust: 100%;
  font-weight: 400;
  font-variant: tabular-nums;
  line-height: 1.5;
  font-feature-settings: "tnum";
  background-color: #FFFEFC;
  color: $font-color-primary;
}

#initial-spinner {
  display: none !important;
}

.app-spinner {
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
